import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { delay, filter, map, switchMap, take } from 'rxjs/operators';
import * as fromRoot from './reducers';
import * as messageActions from './shared/message/message.actions';
import * as fromMessage from './shared/message/message.reducer';
import { Message } from './shared/message/message.reducer';
import { environment } from '../environments/environment';
import { ServiceUnavailableService } from './shared/services/service-unavailable.service';
import { LocalizeService } from './shared/utils/localize.service';

@Component({
    selector: 'dmv-pub-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private titleSubscription: Subscription;
    private languageChangedSubscription: Subscription;
    private routeChangedSubscription: Subscription;
    private cookieBannerObserver: MutationObserver;

    public themeClass: string;
    public message$: Observable<Message>;
    public showSubfooter$: Observable<boolean>;
    public bannerTekst$: Observable<string>;
    public hasChild: boolean;

    constructor(
        private overlayContainer: OverlayContainer,
        private title: Title,
        private translate: TranslateService,
        private store: Store<fromRoot.State>,
        private meta: Meta,
        private serviceUnavailable: ServiceUnavailableService,
        private localizeService: LocalizeService
    ) {
    }

    public ngOnInit(): void {
        // to support full theme switching, get this from redux store and respond to theme changes
        this.setTheme('default-theme');

        this.message$ = this.store.pipe(select(fromMessage.selectMessage));
        this.titleSubscription = this.translate.stream('Site.Title').subscribe(t => this.title.setTitle(t));
        this.routeChangedSubscription = this.store.pipe(select(fromRoot.getCurrentRoute), delay(100))
            .subscribe((route: string) => {
                this.imitateTargetBehaviour(route);
                this.setA11yMessage(route);
            });
        this.showSubfooter$ = this.store.pipe(select(fromRoot.getCurrentRoute),
            map(value => /^(\/flows\/).*/.test(value)));

        this.cookieBannerObserver = new MutationObserver(() => {
            if (document.querySelector('.cky-btn-accept')) {
                this.cookieBannerObserver.disconnect();
                this.setCookieBannerTabIndices();
            }
        });
        this.cookieBannerObserver.observe(document.body, { childList: true, subtree: true });

        this.translate.get('lead').pipe(take(1), filter(value => value !== 'lead'))
            .subscribe(value => this.meta.addTag({name: 'description', content: value}));

        this.bannerTekst$ = this.serviceUnavailable.getBannerTekst(this.localizeService.getLanguage());
    }

    private setCookieBannerTabIndices(): void {
        const order = ['.cky-btn-customize', '.cky-btn-reject', '.cky-btn-accept'];
        order.forEach((selector, index) => {
            const element = document.querySelector(selector) as HTMLElement;
            if (element) {
                element.tabIndex = index + 1;
            }
        });
    }

    public ngOnDestroy(): void {
        this.cookieBannerObserver?.disconnect();

        if (this.languageChangedSubscription) {
            this.languageChangedSubscription.unsubscribe();
        }

        if (this.titleSubscription) {
            this.titleSubscription.unsubscribe();
        }

        if (this.routeChangedSubscription) {
            this.routeChangedSubscription.unsubscribe();
        }
    }

    private setTheme(themeName: string): void {
        this.themeClass = themeName;

        // remove old theme classes and set new
        const classList = this.overlayContainer.getContainerElement().classList;
        const toRemove = Array.from(classList).filter((item: string) => item.includes('-theme'));

        if (toRemove.length) {
            classList.remove(...toRemove);
        }

        classList.add(this.themeClass);
    }

    /**
     * Angular routerlink fragment cannot be used for accessibility purposes:
     * it does not place the focus on the target element.
     * Make sure your target element has tabindex set to -1!
     */
    private imitateTargetBehaviour(route: string): void {
        const hash = route.match(/#\w*$/);
        if (!hash || !hash.length) {
            return;
        }
        const target = document.querySelector(hash[0]) as HTMLElement;
        if (!target) {
            return;
        }
        target.focus();
    }

    /**
     * Adds a visually hidden aria-live region that announces page load.
     */
    private setA11yMessage(route: string): void {
        this.translate
            .get(`Pages.${route}`)
            .pipe(switchMap((pageTitle: string) =>
                this.translate.get('Pages.loaded', {name: pageTitle !== `Pages.${route}` ? pageTitle : route}))
            )
            .subscribe((message: string) =>
                this.store.dispatch(new messageActions.SetMessage({text: message, hidden: true})));
    }

    public get isProduction(): boolean {
        return environment.production || this.env === 'pr';
    }

    public get env(): string {
        return environment.env;
    }
}
